import { Tag, TagID, TagData, TagType } from 'Types/tag'

export const TagsSFW: TagType[] = []

export const TagsNSFW: TagType[] = []

export const CreateCharacterTagsSFW: TagType[] = []

export const CreateCharacterTagsNSFW: TagType[] = []

export const TagMap: Record<string, Tag> = {}

const AllActiveTagsSFW: TagType[] = []

const AllActiveTagsNSFW: TagType[] = []

export const TagLookupSFW: TagType[] = AllActiveTagsSFW

export const TagLookupNSFW: TagType[] = AllActiveTagsNSFW

let _initialized = false
let _loaded = false

const normalizeTimestampMs = (value?: number | string | null) => {
  const timestamp = typeof value === 'string' ? Number(value) : value
  if (!timestamp || !Number.isFinite(timestamp)) return undefined

  return timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp
}

const isActivityEventTag = (tag: TagData | TagType) => tag.tag_type === 'event'

const canSelectTagForUpdatedCharacter = (
  tag: TagData | TagType,
  createdAt?: number | string | null
) => {
  if (!isActivityEventTag(tag)) return true

  const startTimeSeconds = tag.event_start_time || 0
  const endTimeSeconds = tag.event_end_time || 0
  if (startTimeSeconds <= 0 || endTimeSeconds <= 0) return false

  const nowSeconds = Math.floor(Date.now() / 1000)
  if (nowSeconds < startTimeSeconds || nowSeconds > endTimeSeconds) {
    return false
  }

  const createdAtMs = normalizeTimestampMs(createdAt)

  return (
    typeof createdAtMs === 'number' &&
    createdAtMs >= startTimeSeconds * 1000 &&
    createdAtMs <= endTimeSeconds * 1000
  )
}

export const filterTagsForUpdatedCharacter = <T extends TagData | TagType>(
  tags: T[],
  createdAt?: number | string | null,
  selectedTagIDs: TagID[] = []
) => {
  const selectedTagIDSet = new Set(selectedTagIDs)
  return tags.filter((tag) => {
    const tagId = 'id' in tag ? tag.id : tag.tag_id

    return (
      canSelectTagForUpdatedCharacter(tag, createdAt) ||
      selectedTagIDSet.has(tagId)
    )
  })
}

const shouldShowInFilter = (tag: TagData | TagType) =>
  tag.show_in_filter !== false

const shouldShowInCharacterCreation = (tag: TagData | TagType) =>
  tag.show_in_character_creation !== false && canSelectTagForNewCharacter(tag)

const canSelectTagForNewCharacter = (tag: TagData | TagType) => {
  if (!isActivityEventTag(tag)) return true

  const startTimeSeconds = tag.event_start_time || 0
  const endTimeSeconds = tag.event_end_time || 0
  if (startTimeSeconds <= 0 || endTimeSeconds <= 0) return false

  const nowSeconds = Math.floor(Date.now() / 1000)
  return nowSeconds >= startTimeSeconds && nowSeconds <= endTimeSeconds
}

const updateExportsInPlace = (sfw: TagData[], all: TagData[]) => {
  const sfwList = sfw
    .filter((t) => !t.nsfw)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((t) => ({
      id: t.tag_id as TagID,
      tag: (t.desc || t.alias) as Tag,
      alias: t.alias,
      isNew: t.is_new,
      isEvent: t.is_event,
      icon_type: t.icon_type,
      icon_value: t.icon_value,
      text_color: t.text_color,
      watermark_url: t.watermark_url,
      show_badge_dot: Boolean(t.show_badge_dot),
      show_in_filter: t.show_in_filter !== false,
      show_in_character_creation: t.show_in_character_creation !== false,
      tag_type: t.tag_type || 'regular',
      event_start_time: t.event_start_time || 0,
      event_end_time: t.event_end_time || 0,
    }))
  AllActiveTagsSFW.splice(0, AllActiveTagsSFW.length, ...sfwList)
  TagsSFW.splice(0, TagsSFW.length, ...sfwList.filter(shouldShowInFilter))
  CreateCharacterTagsSFW.splice(
    0,
    CreateCharacterTagsSFW.length,
    ...sfwList.filter(shouldShowInCharacterCreation)
  )

  const allList = all
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((t) => ({
      id: t.tag_id as TagID,
      tag: (t.desc || t.alias) as Tag,
      alias: t.alias,
      isNew: t.is_new,
      isEvent: t.is_event,
      icon_type: t.icon_type,
      icon_value: t.icon_value,
      text_color: t.text_color,
      watermark_url: t.watermark_url,
      show_badge_dot: Boolean(t.show_badge_dot),
      show_in_filter: t.show_in_filter !== false,
      show_in_character_creation: t.show_in_character_creation !== false,
      tag_type: t.tag_type || 'regular',
      event_start_time: t.event_start_time || 0,
      event_end_time: t.event_end_time || 0,
    }))
  AllActiveTagsNSFW.splice(0, AllActiveTagsNSFW.length, ...allList)
  TagsNSFW.splice(0, TagsNSFW.length, ...allList.filter(shouldShowInFilter))
  CreateCharacterTagsNSFW.splice(
    0,
    CreateCharacterTagsNSFW.length,
    ...allList.filter(shouldShowInCharacterCreation)
  )

  Object.keys(TagMap).forEach((k) => delete TagMap[k])
  const allListWithDepercated = all.map((t) => ({
    id: t.tag_id as TagID,
    tag: (t.desc || t.alias) as Tag,
  }))
  for (const item of allListWithDepercated) {
    TagMap[item.id] = item.tag
  }
}

const initTagsFetch = async () => {
  if (_initialized) return
  _initialized = true
  if (typeof window === 'undefined') return
  try {
    const { getCharacterTags } = await import('Apis/character')
    const [sfwTags, allTags] = await Promise.all([
      getCharacterTags(false),
      getCharacterTags(true),
    ])
    if (Array.isArray(sfwTags) && Array.isArray(allTags)) {
      updateExportsInPlace(sfwTags as TagData[], allTags as TagData[])
      _loaded = true
    }
  } catch (e) {
    // 不抛错影响页面渲染
    console.warn('Failed to fetch character tags.', e)
  }
}

if (typeof window !== 'undefined') {
  initTagsFetch()
    .then()
    .catch(() => {})
}

const getTagListForRating = (nsfw: boolean) =>
  nsfw ? AllActiveTagsNSFW : AllActiveTagsSFW

export const filterTagIDsByRating = (tagIDs: TagID[], nsfw: boolean) => {
  const tagList = getTagListForRating(nsfw)
  if (!_loaded || tagList.length === 0) {
    return tagIDs
  }

  const allowedIDs = new Set(tagList.map((tag) => tag.id))
  return tagIDs.filter((id) => allowedIDs.has(id))
}

export const ensureTagsLoaded = () => initTagsFetch()
